import TextInput from '../TextInput';
import { ExclamationCircleIcon, EnvelopeIcon, PhoneIcon, ChatBubbleLeftIcon } from '@heroicons/react/24/outline';
import InfoMessageBlock from '~/components/InfoMessageBlock.jsx';
import { FEATURE_MARKETING } from '~/lib/Features.js';
import { formatDateTime } from '~/lib/DateTime.js';
import useBusinessPostApi from '../../lib/useBusinessPostApi.js';
import * as Toast from '../../lib/Toast.js';
import Toggle from '../Toggle.jsx';
import { useEffect, useState } from 'react';


const ClientEditForm = ({ newClientData, setNewClientData, isUnsubscribed, client, businessFeatures, onResubscribe }) => {
    const [marketingEnabled, setMarketingEnabled] = useState(!!client?.settings?.marketing_opt_in);
    useEffect(() => {
        setMarketingEnabled(!!client?.settings?.marketing_opt_in);
    }, [client?.settings?.marketing_opt_in]);

    const { save: resubscribe, isSaving: isResubscribing } = useBusinessPostApi(`/clients/${client?.id}/resubscribe`);
    const { save: optIn, isSaving: isOptingIn } = useBusinessPostApi(`/clients/${client?.id}/marketing-opt-in`);
    const { save: optOut, isSaving: isOptingOut } = useBusinessPostApi(`/clients/${client?.id}/marketing-opt-out`);

    const handleResubscribe = () => {
        resubscribe({}).then(() => {
            Toast.success('Client has been re-subscribed to emails');
            if (onResubscribe) {
                onResubscribe();
            }
        }).catch((error) => {
            Toast.error('Failed to re-subscribe client');
        });
    };


    return (
        <div>
            <div className="flex items-center mt-4">
                <TextInput
                    id="firstName"
                    label="First Name"
                    className="mr-2"
                    inlineEditable={true}
                    autoFocus={!newClientData.firstName}
                    value={newClientData.firstName}
                    onChange={value =>
                        setNewClientData({ ...newClientData, firstName: value })
                    }
                />

                <TextInput
                    id="lastName"
                    label="Last Name"
                    inlineEditable={true}
                    value={newClientData.lastName}
                    onChange={value =>
                        setNewClientData({ ...newClientData, lastName: value })
                    }
                />
            </div>

            <div className="mb-4 mt-4">
                <div className="flex items-end gap-1">
                    <div className="flex-1">
                        <TextInput
                            label="Email"
                            id="email"
                            inlineEditable={true}
                            value={newClientData.email}
                            onChange={value =>
                                setNewClientData({ ...newClientData, email: value })
                            }
                        />
                    </div>
                    {newClientData.email && (
                        <a href={`mailto:${newClientData.email}`} className="mb-1 p-1.5 text-primary-700" title="Send email">
                            <EnvelopeIcon className="w-5 h-5" />
                        </a>
                    )}
                </div>

                {isUnsubscribed && (
                    <InfoMessageBlock className="flex items-center justify-between text-sm">
                        <div className="flex items-center">
                            <ExclamationCircleIcon className="w-5 h-5 mr-2" />
                            This client has unsubscribed from emails
                        </div>
                        <button
                            type="button"
                            className="btn-inverse-pill-sm text-nowrap"
                            onClick={handleResubscribe}
                            disabled={isResubscribing}
                        >
                            {isResubscribing ? 'Re-subscribing...' : 'Re-subscribe'}
                        </button>
                    </InfoMessageBlock>
                )}

                {businessFeatures?.[FEATURE_MARKETING] && (
                    <div className="mt-2 panel p-4">
                        <div className="flex items-center justify-between text-sm">
                            <div className="text-gray-700">Subscribe to marketing emails</div>
                            <Toggle
                                enabled={marketingEnabled}
                                setEnabled={(val) => {
                                    if (isOptingIn || isOptingOut) return;
                                    setMarketingEnabled(val);
                                    const action = val ? optIn : optOut;
                                    action({})
                                        .then(() => {
                                            Toast.success(val ? 'Client has been opted in to marketing emails' : 'Client marketing opt-in revoked');
                                            onResubscribe?.();
                                        })
                                        .catch(() => {
                                            setMarketingEnabled(!val);
                                            Toast.error(val ? 'Failed to opt-in client to marketing emails' : 'Failed to revoke marketing opt-in');
                                        });
                                }}
                                className={(isOptingIn || isOptingOut) ? 'opacity-60 pointer-events-none' : ''}
                            />
                        </div>
                        {marketingEnabled && client?.settings?.marketing_opt_in_date && (
                            <div className="text-xs text-gray-500 mt-1">
                                Opted in to receive marketing emails on {formatDateTime(client.settings.marketing_opt_in_date).split(' at')[0]}
                            </div>
                        )}
                        {!marketingEnabled && (
                            <div className="text-xs text-gray-500 mt-1 pr-12">
                                Not opted in to marketing emails. This should only be enabled if the client has explicitly agreed to opt in.
                            </div>
                        )}
                    </div>
                )}
            </div>
            <div className="mb-4">
                <div className="flex items-end gap-1">
                    <div className="flex-1">
                        <TextInput
                            label="Phone"
                            id="phone"
                            inlineEditable={true}
                            value={newClientData.phone}
                            onChange={value =>
                                setNewClientData({ ...newClientData, phone: value })
                            }
                        />
                    </div>
                    {newClientData.phone && (
                        <>
                            <a href={`tel:${newClientData.phone}`} className="mb-1 p-1.5 text-primary-700" title="Call phone number">
                                <PhoneIcon className="w-5 h-5" />
                            </a>
                            <a href={`sms:${newClientData.phone}`} className="mb-1 p-1.5 text-primary-700" title="Send text message">
                                <ChatBubbleLeftIcon className="w-5 h-5" />
                            </a>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ClientEditForm;
